import { useState, useEffect } from "react";
import { itemsToExclude } from "../../utils/mappings";
import { useModalStack } from "../../utils/modalContext";
import { useCurrencies } from "../../hooks/useCurrencies";

const EnergyItems = ({ items }) => {
  const [sortConfig, setSortConfig] = useState({ key: "energyValue", direction: "descending" });
  const [searchTerm, setSearchTerm] = useState("");
  const [coinsIcon, setCoinsIcon] = useState(null);
  const { pushModal, popModal } = useModalStack();
  const { data } = useCurrencies();

  useEffect(() => {
    if (!data) return;

    const coin = data.find((cur) => cur.id === "cur_coins");
    if (coin) {
      setCoinsIcon(coin.image);
    }
  }, [data]);

  const handleOpenModal = async (itemIndex) => {
    const item = sortedItems[itemIndex];
    if (!item) return;
  
    try {
      pushModal({
        id: `item-${item._id}`,
        type: "itemModal",
        props: {
          item: item.data,
          index: itemIndex,
          totalItems: sortedItems.length,
          showNavigation: true,
          onNavigate: (direction) => handleNavigate(direction, itemIndex),
          onClose: () => popModal(),
        },
      });
    } catch (err) {
      console.error("Failed to open item modal:", err);
    }
  };  

  const handleNavigate = (direction, currentIndex) => {
    const newIndex = direction === "next"
      ? (currentIndex + 1) % sortedItems.length
      : (currentIndex - 1 + sortedItems.length) % sortedItems.length;
  
    const newItem = sortedItems[newIndex];
    if (!newItem) return;
    console.log(newItem);
  
    popModal();
  
    pushModal({
      id: `item-${newItem._id}`,
      type: "itemModal",
      props: {
        item: newItem.data,
        index: newIndex,
        totalItems: sortedItems.length,
        showNavigation: true,
        onNavigate: (dir) => handleNavigate(dir, newIndex),
        onClose: () => popModal(),
      },
    });
  };
  

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "ascending" ? "descending" : "ascending",
        };
      }
      return { key, direction: "ascending" };
    });
  };

  const filteredItems = items
    .filter((item) => !itemsToExclude.includes(item.data.id))
    .filter((item) => item.data.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .map((item) => {
      const ingredientCost = item.data.ingredientCost || 0;
      const craftingCost = item.data.craftingCost || 0;
      const energy = item.data.energyValue || 0;
      const craftingEnergy = item.data.craftingEnergy || 0;
      const netEnergy = energy - craftingEnergy;

      const storePrice = item?.data?.stores?.[0]?.price ?? Infinity;
      const marketPrice = item?.data?.baseMarketPrice ?? Infinity;
      const bestPrice = Math.min(storePrice > 0 ? storePrice : Infinity, marketPrice > 0 ? marketPrice : Infinity);
      const energyPerCraft = craftingCost > 0 ? craftingCost / energy : null;
      const energyPerBuy = bestPrice > 0 && bestPrice !== Infinity ? bestPrice / energy : null;
      const name = item.data.name;

      return {
        ...item,
        name,
        netEnergy,
        ingredientCost,
        craftingCost,
        bestPrice,
        energyPerCraft,
        energyPerBuy,
      };
    });

    const sortedItems = filteredItems.sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
    
      // Handle nulls for "N/A"
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;
    
      // Special handling for baseEnergy column
      if (sortConfig.key === "energyValue") {
        const numA = Number(aVal);
        const numB = Number(bVal);
        return sortConfig.direction === "ascending" ? numA - numB : numB - numA;
      }
    
      if (typeof aVal === "string" && typeof bVal === "string") {
        return sortConfig.direction === "ascending" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
    
      // Default numeric comparison
      return sortConfig.direction === "ascending" ? aVal - bVal : bVal - aVal;
    });

  const renderHeader = (label, key) => (
    <th className="p-2 border cursor-pointer" onClick={() => handleSort(key)}>
      {label} {sortConfig.key === key ? (sortConfig.direction === "ascending" ? "▲" : "▼") : ""}
    </th>
  );

  return (
    <div>
      <input
        type="text"
        placeholder="Search item name..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="p-2 mb-4 border rounded w-full text-xs text-[var(--text3-color)]"
      />
      <div className="overflow-auto h-[50svh]">
        <table className="market-table min-w-full border-collapse border">
          <thead>
            <tr>
              {renderHeader("Name", "name")}
              {renderHeader("Base Energy", "energyValue")}
              {renderHeader("Net Energy", "netEnergy")}
              {renderHeader("Ingredient Cost", "ingredientCost")}
              {renderHeader("Total Cost", "craftingCost")}
              {renderHeader("Buy Price", "bestPrice")}
              {renderHeader("CPE (Craft)", "energyPerCraft")}
              {renderHeader("CPE (Buy)", "energyPerBuy")}
            </tr>
          </thead>
          <tbody>
            {sortedItems.map((item, index) => (
              <tr key={item.id}>
                <td className="cursor-pointer" onClick={() => handleOpenModal(index)}>
                  <img src={item.image} alt={item.name} className="w-8 h-8 inline-block mr-2" />
                  {"  "}{item.name}
                </td>
                <td>{item.energyValue}</td>
                <td>{item.netEnergy}</td>
                <td>{item.ingredientCost} <img src={coinsIcon} alt="coin" className="w-3 inline"/></td>
                <td>{item.craftingCost} <img src={coinsIcon} alt="coin" className="w-3 inline"/></td>
                <td>{item.bestPrice !== Infinity ? new Intl.NumberFormat().format(item.bestPrice) : 0} <img src={coinsIcon} alt="coin" className="w-3 inline"/></td>
                <td>{item.energyPerCost !== null ? new Intl.NumberFormat().format(item.energyPerCraft) : "N/A"}</td>
                <td>{item.energyPerBuy !== null ? new Intl.NumberFormat().format(item.energyPerBuy) : "N/A"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EnergyItems;
